// Choose a new password, from the link in a reset email (scenario A2, B-306).
//
// The link is `/reset#<token>`. The token rides in the fragment because a
// browser sends a fragment nowhere — not to the server's logs, not in a
// Referer — and this screen takes it out of the address bar as soon as it has
// read it: for an hour it is a key to the account, and an address bar is
// something people screenshot and share.
//
// Three states, one card each (the Wizard, one step at a time):
//   choose  a new password, which signs this device in and every other out;
//   ask     no link, or one that has run out: an email for a new one;
//   sent    what happens next, worded for any address (B-306).

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { newPasswordRefusal, PASSWORD_RULE } from './shared/passwordRule';
import { useSession } from '../data/session';
import { useTier } from '../shell/useTier';
import { Wizard, type WizardStep } from '../templates/Wizard';
import { PasswordField, Text, TextField, tell } from '../kit/ui';

/** A token as `newToken` makes them: base64url, 43 characters for 32 bytes. */
const TOKEN = /^[A-Za-z0-9_-]{20,}$/;

type Stage = 'choose' | 'ask' | 'sent';

export function ResetScreen() {
  const { confirmReset, requestReset } = useSession();
  const tier = useTier();
  const navigate = useNavigate();
  const location = useLocation();
  // Read once, on the first render: the effect below clears the bar.
  const [token] = useState<string | null>(() => {
    const raw = location.hash.replace(/^#/, '');
    return TOKEN.test(raw) ? raw : null;
  });
  const [stage, setStage] = useState<Stage>(token === null ? 'ask' : 'choose');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** Why the ask card is showing: no link at all, or the server's word on this one. */
  const [why, setWhy] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    if (location.hash !== '') navigate(location.pathname, { replace: true });
  }, [location.hash, location.pathname, navigate]);

  const save = async () => {
    if (pending || token === null) return;
    const refusal = newPasswordRefusal(password);
    if (refusal !== null) return setError(refusal);
    setPending(true);
    const outcome = await confirmReset(token, password);
    setPending(false);
    if (outcome.ok) {
      setPassword('');
      tell('New password saved. You are signed in here and signed out everywhere else.', 'good');
      navigate('/', { replace: true });
      return;
    }
    // The link itself is spent, expired or unknown: no password will help,
    // so the card becomes the way to a new one. Anything else — a password
    // refused, a throttle, no connection — stays here to try again.
    if (outcome.code === 'unauthorized' || outcome.code === 'not_found') {
      setWhy(outcome.message);
      setError(null);
      setStage('ask');
      return;
    }
    setError(outcome.message);
  };

  const ask = async () => {
    if (pending) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      return setError('That email does not look right yet.');
    }
    setPending(true);
    const outcome = await requestReset(email);
    setPending(false);
    if (!outcome.ok) return setError(outcome.message);
    setError(null);
    setStage('sent');
  };

  const steps: Record<Stage, WizardStep> = {
    choose: {
      id: 'choose',
      title: 'Choose a new password',
      hint: 'Saving it signs you in here and signs you out on every other device.',
      content: (
        <PasswordField
          label="New password"
          description={PASSWORD_RULE}
          value={password}
          onChange={(next) => {
            setError(null);
            setPassword(next);
          }}
          autoComplete="new-password"
        />
      ),
      error,
      doneLabel: pending ? 'Saving…' : 'Save and sign in',
    },
    ask: {
      id: 'ask',
      title: why === null ? 'Get a reset link' : 'That link will not work',
      hint:
        why === null
          ? 'This page opens from the link in a reset email. Type the email you sign in with and we will send one.'
          : `${why} Type the email you sign in with and we will send a new one.`,
      content: (
        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          value={email}
          onChange={(next) => {
            setError(null);
            setEmail(next);
          }}
          autoFocus
        />
      ),
      error,
      doneLabel: pending ? 'Sending…' : 'Email me a link',
    },
    sent: {
      id: 'sent',
      title: 'Check your email',
      hint: `If ${email.trim()} has an account here, a link to choose a new password is on its way. It works for one hour.`,
      content: (
        <Text tone="secondary" size="sm" block>
          Nothing after a few minutes? Check your spam folder, or come back here to send another.
        </Text>
      ),
      doneLabel: 'Done',
    },
  };

  // ITS OWN FRAME, as the front page has: this route is outside the shell, and
  // without the frame's full height the Wizard's bar sits under the card
  // instead of at the foot of the screen.
  return (
    <div className="fsp-frame" data-tier={tier}>
      <Wizard
        title="Reset your password"
        steps={[steps[stage]]}
        index={0}
        onIndex={() => undefined}
        onDone={() => {
          if (stage === 'choose') void save();
          else if (stage === 'ask') void ask();
          else navigate('/', { replace: true });
        }}
        onCancel={() => navigate('/', { replace: true })}
      />
    </div>
  );
}
